import React from "react";
import { motion } from "framer-motion";
import { FaEnvelope, FaCommentDots, FaPhoneAlt, FaBook, FaVideo, FaQuestionCircle, FaFileAlt, FaSmile } from "react-icons/fa";

const supportChannels = [
  { icon: <FaEnvelope />, title: "Email Support", desc: "support@clockinmaster.com", accent: "from-blue-500 to-cyan-400" },
  { icon: <FaBook />, title: "Help Center", desc: "Comprehensive documentation and guides", accent: "from-violet-500 to-purple-400" },
  { icon: <FaCommentDots />, title: "Live Chat", desc: "Available during business hours", accent: "from-emerald-500 to-teal-400" },
  { icon: <FaPhoneAlt />, title: "Phone Support", desc: "For enterprise customers", accent: "from-amber-500 to-orange-400" },
];

const selfServiceItems = [
  { icon: <FaBook />, title: "Knowledge Base", desc: "In-depth articles and guides" },
  { icon: <FaVideo />, title: "Video Tutorials", desc: "Step-by-step walkthroughs" },
  { icon: <FaQuestionCircle />, title: "FAQs", desc: "Quick answers to common questions" },
  { icon: <FaFileAlt />, title: "User Guides", desc: "Complete documentation" },
];

const Support = () => (
  <div className="content-page">
    {/* Hero Banner */}
    <div className="content-page-header">
      <div className="absolute inset-0 bg-hero-mesh" />
      <div className="container mx-auto px-6 relative z-10 text-center">
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-4xl md:text-5xl font-extrabold mb-3"
          style={{ fontFamily: "'Poppins', sans-serif" }}
        >
          Support Policy
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="text-white/60 text-sm"
        >
          Last updated: May 2025
        </motion.p>
      </div>
    </div>

    {/* Content */}
    <div className="content-page-body pb-16">
      <div className="content-card">
        <Section title="1. Support Overview">
          <p className="text-gray-600 leading-relaxed">
            ClockIn Master is committed to providing excellent customer support to all our users. This policy outlines the types of support we provide, our response times, and how to get help when you need it.
          </p>
        </Section>

        <Section title="2. Support Channels">
          <p className="text-gray-600 mb-5">We offer support through multiple channels:</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-2">
            {supportChannels.map((ch) => (
              <div key={ch.title} className="bg-lightgray rounded-xl p-5 flex items-start gap-4">
                <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${ch.accent} text-white flex items-center justify-center flex-shrink-0`}>
                  {ch.icon}
                </div>
                <div>
                  <h4 className="text-sm font-bold text-gray-900">{ch.title}</h4>
                  <p className="text-xs text-gray-500 mt-0.5">{ch.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </Section>

        <Section title="3. Response Times">
          <p className="text-gray-600 mb-4">Our standard response times are:</p>
          <div className="overflow-x-auto rounded-xl border border-gray-200 mb-2">
            <table className="min-w-full">
              <thead>
                <tr className="bg-lightgray">
                  <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Priority</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Response Time</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {[
                  { priority: "Critical", time: "Within 2 hours", color: "bg-red-100 text-red-700" },
                  { priority: "High", time: "Within 4 hours", color: "bg-amber-100 text-amber-700" },
                  { priority: "Medium", time: "Within 24 hours", color: "bg-blue-100 text-blue-700" },
                  { priority: "Low", time: "Within 48 hours", color: "bg-gray-100 text-gray-700" },
                ].map((item) => (
                  <tr key={item.priority}>
                    <td className="px-5 py-3">
                      <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${item.color}`}>{item.priority}</span>
                    </td>
                    <td className="px-5 py-3 text-sm text-gray-600">{item.time}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>

        <Section title="4. Support Hours">
          <div className="bg-lightgray rounded-xl p-5 mb-2">
            <ul className="space-y-2">
              <li className="flex items-center gap-2 text-sm text-gray-600">
                <span className="w-1.5 h-1.5 rounded-full bg-accent" /> Monday to Friday: 9:00 AM – 6:00 PM EST
              </li>
              <li className="flex items-center gap-2 text-sm text-gray-600">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-400" /> Weekends: Limited support available
              </li>
              <li className="flex items-center gap-2 text-sm text-gray-600">
                <span className="w-1.5 h-1.5 rounded-full bg-gray-400" /> Holidays: Support may be limited
              </li>
            </ul>
          </div>
        </Section>

        <Section title="5. Issue Resolution">
          <p className="text-gray-600 mb-4">We follow a structured approach:</p>
          <div className="flex flex-col gap-3 mb-2">
            {["Issue identification and categorization", "Initial response and troubleshooting", "Problem resolution or workaround", "Follow-up and feedback"].map((step, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-secondary/10 text-secondary text-xs font-bold flex items-center justify-center flex-shrink-0">
                  {i + 1}
                </div>
                <span className="text-sm text-gray-600">{step}</span>
              </div>
            ))}
          </div>
        </Section>

        <Section title="6. Self-Service Resources">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-2">
            {selfServiceItems.map((item) => (
              <div key={item.title} className="bg-lightgray rounded-xl p-4 flex items-start gap-3">
                <div className="text-secondary text-lg mt-0.5">{item.icon}</div>
                <div>
                  <h4 className="text-sm font-bold text-gray-900">{item.title}</h4>
                  <p className="text-xs text-gray-500 mt-0.5">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </Section>

        <Section title="7. Feedback and Improvement">
          <div className="bg-lightgray rounded-xl p-5 flex items-start gap-4 mb-2">
            <FaSmile className="text-accent text-2xl mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-gray-600 text-sm mb-1">We value your feedback to improve our support services. After each interaction, you'll receive a survey to rate your experience.</p>
              <p className="text-gray-600 text-sm">We regularly review feedback to enhance our processes and training.</p>
            </div>
          </div>
        </Section>

        <Section title="8. Contact Support">
          <p className="text-gray-600 text-sm">
            For immediate assistance, please contact our support team at{" "}
            <a href="mailto:hello@clockinmaster.com" className="text-secondary font-semibold hover:underline">hello@clockinmaster.com</a>.
          </p>
        </Section>
      </div>
    </div>
  </div>
);

const Section = ({ title, children }) => (
  <div className="mb-8 last:mb-0">
    <h2 className="text-xl font-bold text-gray-900 mb-4">{title}</h2>
    {children}
  </div>
);

export default Support;